import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import CMBSWaterfallSimulator from './CMBSWaterfallSimulator';

// V298 — RE Debt Phase 2. Option B layout (locked w/ user 2026-05-06):
//   landing  = KPI strip + deal-shelf rollup table + sortable pool table
//   detail   = 4-quadrant Aladdin-style: capital-stack visualizer (top-L),
//              property-mix donut + geo (top-R), pool performance time-series
//              (bottom-L), cushion heatmap (bottom-R).
// Single file, no new patterns; mirrors CLOManagementScreen.tsx for parity.

// ============ Types ============
interface CmbsPool {
  id: number;
  dealName: string;
  dealTicker?: string;
  cusipBase?: string;
  dealShelf?: string;
  dealType?: string;          // CONDUIT / SASB / AGENCY_CMBS
  status?: string;            // ACTIVE / AMORTIZING / etc.
  masterServicer?: string;
  specialServicer?: string;
  trusteeName?: string;
  closingDate?: string;
  expectedFinalDistribution?: string;
  ratedFinalDistribution?: string;
  originalPoolBalance?: number;
  currentPoolBalance?: number;
  numLoansAtIssuance?: number;
  numLoansRemaining?: number;
  currentWalaMonths?: number;
  currentWamMonths?: number;
  currentWacPct?: number;
  top5LoanConcentrationPct?: number;
  top10LoanConcentrationPct?: number;
  largestLoanPct?: number;
  delinquency30Pct?: number;
  delinquency60Pct?: number;
  delinquency90PlusPct?: number;
  speciallyServicedPct?: number;
  defeasedPct?: number;
  prepaidPct?: number;
  cumulativeRealizedLoss?: number;
  cumulativeRealizedLossPct?: number;
  poolFactor?: number;
  geoConcentration?: string; // JSONB stringified
}

interface CmbsTranche {
  id: number;
  trancheName: string;
  trancheClass?: string;
  paymentPriority?: number;
  cusip?: string;
  isIo?: boolean;
  isPrincipalOnly?: boolean;
  originalBalance?: number;
  currentBalance?: number;
  notionalBalance?: number;
  originalCreditSupportPct?: number;
  currentCreditSupportPct?: number;
  couponType?: string;
  couponRate?: number;
  spreadBps?: number;
  moodysRating?: string;
  spRating?: string;
  fitchRating?: string;
  kbraRating?: string;
  dbrsRating?: string;
  morningstarRating?: string;
  attachmentPoint?: number;
  detachmentPoint?: number;
  weightedAvgLifeYears?: number;
  expectedMaturity?: string;
  legalFinalMaturity?: string;
}

interface PropertyTypeRow {
  id: number;
  propertyType: string;
  allocationPct: number;
  allocationBalance?: number;
  numLoans?: number;
  asOfDate: string;
}

interface PoolPeriod {
  id: number;
  distributionDate: string;
  poolBalance?: number;
  poolFactor?: number;
  numLoansRemaining?: number;
  walaMonths?: number;
  wamMonths?: number;
  wacPct?: number;
  delinquency30Pct?: number;
  delinquency60Pct?: number;
  delinquency90PlusPct?: number;
  speciallyServicedPct?: number;
  cumulativeRealizedLoss?: number;
  cumulativeRealizedLossPct?: number;
}

interface TrancheCushion {
  trancheId: number;
  trancheName: string;
  trancheClass?: string;
  currentCreditSupportPct?: number;
  triggerPct?: number;
  cushionPct?: number;
  status: string; // PASS / FAIL / UNKNOWN
}

interface CmbsStats {
  totalPools: number;
  activePools: number;
  totalTranches: number;
  totalCurrentPoolBalance?: number;
  totalCumulativeLoss?: number;
  avgDelinquency90PlusPct?: number;
  distinctShelves: number;
}

// ============ Styled (parity with CLOManagementScreen) ============
const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${p => (p.$embedded ? '1rem' : '2rem')};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.4rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Crumb = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  padding: 0.4rem 0.85rem;
  border-radius: 6px;
  font-size: 0.78rem;
  cursor: pointer;
  &:hover { color: white; border-color: #38bdf8; }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.92) 0%, rgba(51, 65, 85, 0.92) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 0.95rem 1.1rem;
  border-left: 3px solid ${p => p.$color || '#38bdf8'};
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.35rem;
  font-weight: 700;
`;

const Section = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(51, 65, 85, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.div`
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  th, td { padding: 0.55rem 0.7rem; text-align: left; }
  thead th {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.66rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  tbody tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    color: #e2e8f0;
  }
  tbody tr:hover {
    background: rgba(56, 189, 248, 0.04);
    cursor: pointer;
  }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  th.num { text-align: right; }
`;

const StatusPill = styled.span<{ $status?: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 0.64rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  ${p => {
    switch ((p.$status || '').toUpperCase()) {
      case 'ACTIVE':     return 'background: rgba(16,185,129,0.12); color: #34d399; border: 1px solid rgba(16,185,129,0.25);';
      case 'AMORTIZING': return 'background: rgba(245,158,11,0.12); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25);';
      case 'CALLED':
      case 'MATURED':    return 'background: rgba(148,163,184,0.12); color: #cbd5e1; border: 1px solid rgba(148,163,184,0.25);';
      case 'DEFEASED':   return 'background: rgba(168,85,247,0.12); color: #c4b5fd; border: 1px solid rgba(168,85,247,0.25);';
      case 'PASS':       return 'background: rgba(16,185,129,0.12); color: #34d399; border: 1px solid rgba(16,185,129,0.25);';
      case 'FAIL':       return 'background: rgba(239,68,68,0.12);  color: #fca5a5; border: 1px solid rgba(239,68,68,0.25);';
      default:           return 'background: rgba(148,163,184,0.10); color: #94a3b8; border: 1px solid rgba(148,163,184,0.20);';
    }
  }}
`;

const QuadrantGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 1.25rem;
  @media (max-width: 1280px) { grid-template-columns: 1fr; }
`;

const KvGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.7rem 1.5rem;
  font-size: 0.82rem;
`;

const Kv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  span:first-child {
    color: #94a3b8;
    font-size: 0.66rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  span:last-child { color: white; font-weight: 600; }
`;

const Empty = styled.div`
  color: #94a3b8;
  text-align: center;
  padding: 2rem;
  font-size: 0.85rem;
`;

// ============ Helpers ============
const fmtCurrency = (v?: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};
const fmtPct = (v?: number | null, dp = 2) => v == null ? '—' : `${v.toFixed(dp)}%`;
const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString() : '—';

// CS-trigger color logic for cushion heatmap
const cushionColor = (cushion?: number) => {
  if (cushion == null) return '#475569';
  if (cushion >= 5)  return '#10b981';   // healthy
  if (cushion >= 0)  return '#84cc16';   // tight pass
  if (cushion >= -2) return '#f59e0b';   // amber breach
  return '#ef4444';                       // hard breach
};

const PROPERTY_COLORS: Record<string, string> = {
  OFFICE: '#3b82f6',
  RETAIL: '#f59e0b',
  INDUSTRIAL: '#06b6d4',
  MULTIFAMILY: '#10b981',
  HOTEL: '#ef4444',
  MIXED_USE: '#8b5cf6',
  SELF_STORAGE: '#a3e635',
  HEALTHCARE: '#ec4899',
  DATA_CENTER: '#14b8a6',
  MANUFACTURED_HOUSING: '#fb923c',
  OTHER: '#94a3b8',
};

// ============ Component ============
interface Props { embedded?: boolean }

const CMBSManagementScreen: React.FC<Props> = ({ embedded = false }) => {
  const [pools, setPools] = useState<CmbsPool[]>([]);
  const [stats, setStats] = useState<CmbsStats | null>(null);
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Sort state for landing pool table
  const [sortKey, setSortKey] = useState<keyof CmbsPool>('currentPoolBalance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => { fetchLanding(); }, []);

  const fetchLanding = async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get('/api/credit/cmbs/pools'),
        axios.get('/api/credit/cmbs/stats').catch(() => ({ data: null })),
      ]);
      setPools(Array.isArray(pRes.data) ? pRes.data : []);
      setStats(sRes.data || null);
    } catch (e) {
      console.error('Failed to load CMBS landing:', e);
    } finally {
      setLoading(false);
    }
  };

  const sortedPools = useMemo(() => {
    const arr = [...pools];
    arr.sort((a, b) => {
      const av = a[sortKey] as any; const bv = b[sortKey] as any;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [pools, sortKey, sortDir]);

  const shelfRollup = useMemo(() => {
    const m = new Map<string, { shelf: string; pools: number; balance: number; cumLoss: number; dq90: number; dqWeight: number }>();
    pools.forEach(p => {
      const k = p.dealShelf || '—';
      const cur = m.get(k) || { shelf: k, pools: 0, balance: 0, cumLoss: 0, dq90: 0, dqWeight: 0 };
      cur.pools += 1;
      cur.balance += p.currentPoolBalance || 0;
      cur.cumLoss += p.cumulativeRealizedLoss || 0;
      if (p.delinquency90PlusPct != null && p.currentPoolBalance) {
        cur.dq90 += p.delinquency90PlusPct * p.currentPoolBalance;
        cur.dqWeight += p.currentPoolBalance;
      }
      m.set(k, cur);
    });
    return Array.from(m.values())
      .map(r => ({ ...r, dq90: r.dqWeight > 0 ? r.dq90 / r.dqWeight : 0 }))
      .sort((a, b) => b.balance - a.balance);
  }, [pools]);

  const setSort = (k: keyof CmbsPool) => {
    if (k === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };
  const sortInd = (k: keyof CmbsPool) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ============ Landing render ============
  if (selectedPoolId == null) {
    return (
      <Container $embedded={embedded}>
        <Header>
          <Title>🏢 CMBS — Pools & Tranches</Title>
        </Header>

        <StatsRow>
          <StatCard $color="#38bdf8"><StatLabel>Total Pools</StatLabel><StatValue>{stats?.totalPools ?? pools.length}</StatValue></StatCard>
          <StatCard $color="#10b981"><StatLabel>Active</StatLabel><StatValue>{stats?.activePools ?? 0}</StatValue></StatCard>
          <StatCard $color="#a78bfa"><StatLabel>Pool Balance</StatLabel><StatValue>{fmtCurrency(stats?.totalCurrentPoolBalance)}</StatValue></StatCard>
          <StatCard $color="#fbbf24"><StatLabel>Wtd 90+ DQ</StatLabel><StatValue>{fmtPct(stats?.avgDelinquency90PlusPct, 2)}</StatValue></StatCard>
          <StatCard $color="#ef4444"><StatLabel>Cum. Loss</StatLabel><StatValue>{fmtCurrency(stats?.totalCumulativeLoss)}</StatValue></StatCard>
          <StatCard $color="#94a3b8"><StatLabel>Tranches</StatLabel><StatValue>{stats?.totalTranches ?? 0}</StatValue></StatCard>
        </StatsRow>

        {shelfRollup.length > 0 && (
          <Section>
            <SectionTitle>📚 Deal Shelves</SectionTitle>
            <Table>
              <thead>
                <tr>
                  <th>Shelf</th>
                  <th className="num">Pools</th>
                  <th className="num">Balance</th>
                  <th className="num">Wtd 90+ DQ</th>
                  <th className="num">Cum. Loss</th>
                </tr>
              </thead>
              <tbody>
                {shelfRollup.map(r => (
                  <tr key={r.shelf}>
                    <td><strong>{r.shelf}</strong></td>
                    <td className="num">{r.pools}</td>
                    <td className="num">{fmtCurrency(r.balance)}</td>
                    <td className="num">{fmtPct(r.dq90, 2)}</td>
                    <td className="num">{fmtCurrency(r.cumLoss)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        )}

        <Section>
          <SectionTitle>🏢 Pools</SectionTitle>
          {loading ? <Empty>Loading…</Empty> : pools.length === 0 ? (
            <Empty>No CMBS pools yet. Pools appear here once a deal is ingested from trustee data.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th onClick={() => setSort('dealName')}>Deal{sortInd('dealName')}</th>
                  <th onClick={() => setSort('dealShelf')}>Shelf{sortInd('dealShelf')}</th>
                  <th onClick={() => setSort('dealType')}>Type{sortInd('dealType')}</th>
                  <th>Status</th>
                  <th className="num" onClick={() => setSort('currentPoolBalance')}>Balance{sortInd('currentPoolBalance')}</th>
                  <th className="num" onClick={() => setSort('numLoansRemaining')}># Loans{sortInd('numLoansRemaining')}</th>
                  <th className="num" onClick={() => setSort('currentWamMonths')}>WAM{sortInd('currentWamMonths')}</th>
                  <th className="num" onClick={() => setSort('delinquency90PlusPct')}>90+ DQ{sortInd('delinquency90PlusPct')}</th>
                  <th className="num" onClick={() => setSort('cumulativeRealizedLossPct')}>Cum Loss %{sortInd('cumulativeRealizedLossPct')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedPools.map(p => (
                  <tr key={p.id} onClick={() => setSelectedPoolId(p.id)}>
                    <td><strong>{p.dealName}</strong>{p.dealTicker && <span style={{ color: '#64748b' }}> · {p.dealTicker}</span>}</td>
                    <td>{p.dealShelf || '—'}</td>
                    <td>{p.dealType || '—'}</td>
                    <td><StatusPill $status={p.status}>{p.status || '—'}</StatusPill></td>
                    <td className="num">{fmtCurrency(p.currentPoolBalance)}</td>
                    <td className="num">{p.numLoansRemaining ?? '—'}</td>
                    <td className="num">{p.currentWamMonths != null ? `${p.currentWamMonths.toFixed(1)} mo` : '—'}</td>
                    <td className="num">{fmtPct(p.delinquency90PlusPct, 2)}</td>
                    <td className="num">{fmtPct(p.cumulativeRealizedLossPct, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Section>
      </Container>
    );
  }

  return <CMBSPoolDetail poolId={selectedPoolId} onBack={() => setSelectedPoolId(null)} embedded={embedded} />;
};

// ============ Detail (4-quadrant Aladdin-style) ============
const CMBSPoolDetail: React.FC<{ poolId: number; onBack: () => void; embedded?: boolean }> = ({ poolId, onBack, embedded }) => {
  const [pool, setPool] = useState<CmbsPool | null>(null);
  const [tranches, setTranches] = useState<CmbsTranche[]>([]);
  const [propertyMix, setPropertyMix] = useState<PropertyTypeRow[]>([]);
  const [periods, setPeriods] = useState<PoolPeriod[]>([]);
  const [cushions, setCushions] = useState<TrancheCushion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [poolRes, tRes, pmRes, perRes, cuRes] = await Promise.all([
          axios.get(`/api/credit/cmbs/pools/${poolId}`),
          axios.get(`/api/credit/cmbs/pools/${poolId}/tranches`),
          axios.get(`/api/credit/cmbs/pools/${poolId}/property-mix`),
          axios.get(`/api/credit/cmbs/pools/${poolId}/periods`),
          axios.get(`/api/credit/cmbs/pools/${poolId}/cushions`),
        ]);
        if (cancelled) return;
        setPool(poolRes.data);
        setTranches(Array.isArray(tRes.data) ? tRes.data : []);
        setPropertyMix(Array.isArray(pmRes.data) ? pmRes.data : []);
        setPeriods(Array.isArray(perRes.data) ? perRes.data : []);
        setCushions(Array.isArray(cuRes.data) ? cuRes.data : []);
      } catch (e) {
        console.error('Failed to load CMBS pool detail:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [poolId]);

  // Capital-stack visualizer geometry
  const capitalStack = useMemo(() => {
    const total = tranches.reduce((s, t) => s + (t.currentBalance || 0), 0);
    if (total <= 0) return [] as { tranche: CmbsTranche; pct: number; cushion?: TrancheCushion }[];
    return [...tranches]
      .sort((a, b) => (a.paymentPriority ?? 99) - (b.paymentPriority ?? 99))
      .map(t => ({
        tranche: t,
        pct: ((t.currentBalance || 0) / total) * 100,
        cushion: cushions.find(c => c.trancheId === t.id),
      }));
  }, [tranches, cushions]);

  const geoEntries = useMemo(() => {
    if (!pool?.geoConcentration) return [] as { state: string; pct: number }[];
    try {
      const obj = typeof pool.geoConcentration === 'string'
        ? JSON.parse(pool.geoConcentration)
        : pool.geoConcentration;
      return Object.entries(obj as Record<string, number>)
        .map(([state, pct]) => ({ state, pct: Number(pct) * (Number(pct) <= 1 ? 100 : 1) }))
        .sort((a, b) => b.pct - a.pct)
        .slice(0, 5);
    } catch {
      return [];
    }
  }, [pool]);

  const periodChartData = useMemo(() => {
    return [...periods]
      .sort((a, b) => a.distributionDate.localeCompare(b.distributionDate))
      .map(p => ({
        date: p.distributionDate,
        balance: p.poolBalance ? p.poolBalance / 1e6 : null,
        dq90: p.delinquency90PlusPct ?? null,
        cumLossPct: p.cumulativeRealizedLossPct ?? null,
      }));
  }, [periods]);

  if (loading) {
    return (
      <Container $embedded={embedded}>
        <Header>
          <Crumb onClick={onBack}>← Pools</Crumb>
          <Title>Loading…</Title>
        </Header>
      </Container>
    );
  }

  if (!pool) {
    return (
      <Container $embedded={embedded}>
        <Header>
          <Crumb onClick={onBack}>← Pools</Crumb>
        </Header>
        <Empty>Pool not found.</Empty>
      </Container>
    );
  }

  return (
    <Container $embedded={embedded}>
      <Header>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Crumb onClick={onBack}>← Pools</Crumb>
            <StatusPill $status={pool.status}>{pool.status || '—'}</StatusPill>
          </div>
          <Title>🏢 {pool.dealName} {pool.dealTicker && <span style={{ color: '#64748b', fontWeight: 400, fontSize: '0.95rem' }}>· {pool.dealTicker}</span>}</Title>
        </div>
      </Header>

      {/* Pool facts strip */}
      <Section>
        <KvGrid>
          <Kv><span>Shelf</span><span>{pool.dealShelf || '—'}</span></Kv>
          <Kv><span>Type</span><span>{pool.dealType || '—'}</span></Kv>
          <Kv><span>Master Servicer</span><span>{pool.masterServicer || '—'}</span></Kv>
          <Kv><span>Special Servicer</span><span>{pool.specialServicer || '—'}</span></Kv>
          <Kv><span>Trustee</span><span>{pool.trusteeName || '—'}</span></Kv>
          <Kv><span>Closing</span><span>{fmtDate(pool.closingDate)}</span></Kv>
          <Kv><span>Expected Final</span><span>{fmtDate(pool.expectedFinalDistribution)}</span></Kv>
          <Kv><span>Pool Factor</span><span>{pool.poolFactor != null ? pool.poolFactor.toFixed(6) : '—'}</span></Kv>
          <Kv><span>Original Bal</span><span>{fmtCurrency(pool.originalPoolBalance)}</span></Kv>
          <Kv><span>Current Bal</span><span>{fmtCurrency(pool.currentPoolBalance)}</span></Kv>
          <Kv><span>Loans @ Issuance</span><span>{pool.numLoansAtIssuance ?? '—'}</span></Kv>
          <Kv><span>Loans Now</span><span>{pool.numLoansRemaining ?? '—'}</span></Kv>
          <Kv><span>Current WAC</span><span>{fmtPct(pool.currentWacPct, 4)}</span></Kv>
          <Kv><span>Current WAM</span><span>{pool.currentWamMonths != null ? `${pool.currentWamMonths.toFixed(1)} mo` : '—'}</span></Kv>
          <Kv><span>30+ DQ</span><span>{fmtPct(pool.delinquency30Pct)}</span></Kv>
          <Kv><span>60+ DQ</span><span>{fmtPct(pool.delinquency60Pct)}</span></Kv>
          <Kv><span>90+ DQ</span><span>{fmtPct(pool.delinquency90PlusPct)}</span></Kv>
          <Kv><span>Specially Serviced</span><span>{fmtPct(pool.speciallyServicedPct)}</span></Kv>
          <Kv><span>Defeased</span><span>{fmtPct(pool.defeasedPct)}</span></Kv>
          <Kv><span>Cum. Loss</span><span>{fmtCurrency(pool.cumulativeRealizedLoss)} ({fmtPct(pool.cumulativeRealizedLossPct)})</span></Kv>
        </KvGrid>
      </Section>

      <QuadrantGrid>
        {/* Top-Left: Capital-stack visualizer */}
        <Section>
          <SectionTitle>🧱 Capital Stack</SectionTitle>
          {capitalStack.length === 0 ? <Empty>No tranches.</Empty> : (
            <CapitalStack stack={capitalStack} />
          )}
        </Section>

        {/* Top-Right: Property mix donut + geo */}
        <Section>
          <SectionTitle>🏗️ Property Mix & Geography</SectionTitle>
          {propertyMix.length === 0 && geoEntries.length === 0 ? <Empty>No property-mix data.</Empty> : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ height: 240 }}>
                {propertyMix.length > 0 ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={propertyMix.map(r => ({ name: r.propertyType, value: r.allocationPct }))}
                        innerRadius={45}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {propertyMix.map((r, i) => (
                          <Cell key={i} fill={PROPERTY_COLORS[r.propertyType] || '#64748b'} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                        formatter={(v: any, n: any) => [`${Number(v).toFixed(2)}%`, n]}
                      />
                      <Legend wrapperStyle={{ fontSize: '0.7rem', color: '#cbd5e1' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <Empty>No property mix.</Empty>}
              </div>
              <div>
                <div style={{ color: '#94a3b8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                  Top 5 States
                </div>
                {geoEntries.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: '0.8rem', paddingTop: '0.5rem' }}>No geo concentration data.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {geoEntries.map(g => (
                      <div key={g.state} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: 'white', fontWeight: 600, width: '32px' }}>{g.state}</span>
                        <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', height: '8px', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(g.pct, 100)}%`, height: '100%', background: '#38bdf8' }} />
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', width: 56, textAlign: 'right' }}>
                          {g.pct.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>

        {/* Bottom-Left: Pool performance time-series */}
        <Section>
          <SectionTitle>📈 Pool Performance</SectionTitle>
          {periodChartData.length === 0 ? <Empty>No trustee-report history yet.</Empty> : (
            <div style={{ height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={periodChartData}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${v.toFixed(0)}M`} />
                  <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `${v.toFixed(1)}%`} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem', color: '#cbd5e1' }} />
                  <Line yAxisId="left"  type="monotone" dataKey="balance"    name="Pool Balance ($M)"  stroke="#38bdf8" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="dq90"       name="90+ DQ %"           stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="cumLossPct" name="Cum. Loss %"        stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        {/* Bottom-Right: Cushion heatmap */}
        <Section>
          <SectionTitle>🛡️ Credit-Support Cushion</SectionTitle>
          {cushions.length === 0 ? <Empty>No tranche cushions to score.</Empty> : (
            <Table>
              <thead>
                <tr>
                  <th>Tranche</th>
                  <th>Class</th>
                  <th className="num">Current CS</th>
                  <th className="num">Trigger</th>
                  <th className="num">Cushion</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {cushions.map(c => (
                  <tr key={c.trancheId}>
                    <td>{c.trancheName}</td>
                    <td>{c.trancheClass || '—'}</td>
                    <td className="num">{fmtPct(c.currentCreditSupportPct, 2)}</td>
                    <td className="num">{fmtPct(c.triggerPct, 2)}</td>
                    <td className="num" style={{ color: cushionColor(c.cushionPct), fontWeight: 700 }}>
                      {c.cushionPct != null ? `${c.cushionPct >= 0 ? '+' : ''}${c.cushionPct.toFixed(2)}` : '—'}
                    </td>
                    <td><StatusPill $status={c.status}>{c.status}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Section>
      </QuadrantGrid>

      {/* Waterfall stress console (Plan B1) */}
      <CMBSWaterfallSimulator
        poolId={pool.id}
        poolName={pool.dealName}
        poolBalance={pool.currentPoolBalance}
      />

      {/* Tranche detail table — full row underneath the quadrants */}
      <Section>
        <SectionTitle>📑 Tranches ({tranches.length})</SectionTitle>
        {tranches.length === 0 ? <Empty>No tranches.</Empty> : (
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Class</th>
                <th>CUSIP</th>
                <th className="num">Priority</th>
                <th className="num">Current Bal</th>
                <th className="num">CS Now</th>
                <th className="num">Coupon</th>
                <th>Moody's / S&amp;P / Fitch</th>
                <th className="num">WAL</th>
              </tr>
            </thead>
            <tbody>
              {[...tranches].sort((a, b) => (a.paymentPriority ?? 99) - (b.paymentPriority ?? 99)).map(t => (
                <tr key={t.id}>
                  <td><strong>{t.trancheName}</strong>{t.isIo && <span style={{ color: '#fbbf24', fontSize: '0.66rem', marginLeft: 6 }}>IO</span>}</td>
                  <td>{t.trancheClass || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{t.cusip || '—'}</td>
                  <td className="num">{t.paymentPriority ?? '—'}</td>
                  <td className="num">{fmtCurrency(t.currentBalance)}</td>
                  <td className="num">{fmtPct(t.currentCreditSupportPct, 2)}</td>
                  <td className="num">{t.couponRate != null ? `${t.couponRate.toFixed(3)}%` : (t.couponType || '—')}</td>
                  <td>{[t.moodysRating, t.spRating, t.fitchRating].filter(Boolean).join(' / ') || '—'}</td>
                  <td className="num">{t.weightedAvgLifeYears != null ? `${t.weightedAvgLifeYears.toFixed(1)} yr` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </Container>
  );
};

// ============ Capital-stack SVG visualizer ============
const CapitalStack: React.FC<{ stack: { tranche: CmbsTranche; pct: number; cushion?: TrancheCushion }[] }> = ({ stack }) => {
  const [hover, setHover] = useState<number | null>(null);
  const W = 320, H = 320;
  const PAD_TOP = 8;
  let yCursor = PAD_TOP;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
      <svg width={W} height={H} style={{ display: 'block' }}>
        {stack.map((row, i) => {
          const h = Math.max(((H - PAD_TOP * 2) * row.pct / 100), 12);
          const y = yCursor;
          yCursor += h;
          const fill = cushionColor(row.cushion?.cushionPct);
          return (
            <g key={row.tranche.id}
               onMouseEnter={() => setHover(i)}
               onMouseLeave={() => setHover(null)}
               style={{ cursor: 'pointer' }}>
              <rect x={20} y={y} width={W - 40} height={h - 2}
                    fill={fill}
                    stroke={hover === i ? '#fff' : 'rgba(255,255,255,0.08)'}
                    strokeWidth={hover === i ? 2 : 1}
                    rx={3} />
              <text x={W / 2} y={y + h / 2 + 4} textAnchor="middle"
                    fill="#0f172a" fontSize={11} fontWeight={700}>
                {row.tranche.trancheName} · {row.pct.toFixed(1)}%
              </text>
            </g>
          );
        })}
      </svg>
      <div style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>
        {hover != null ? (
          <TrancheTip row={stack[hover]} />
        ) : (
          <div style={{ color: '#94a3b8' }}>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Legend</div>
            <LegendDot color="#10b981" label="Cushion ≥ 5%" />
            <LegendDot color="#84cc16" label="Cushion 0–5%" />
            <LegendDot color="#f59e0b" label="Cushion -2 to 0%" />
            <LegendDot color="#ef4444" label="Cushion < -2% (breach)" />
            <div style={{ marginTop: '0.5rem', fontSize: '0.7rem' }}>Hover a tranche for details.</div>
          </div>
        )}
      </div>
    </div>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
    <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: 'inline-block' }} />
    <span style={{ fontSize: '0.75rem' }}>{label}</span>
  </div>
);

const TrancheTip: React.FC<{ row: { tranche: CmbsTranche; pct: number; cushion?: TrancheCushion } }> = ({ row }) => {
  const t = row.tranche; const c = row.cushion;
  return (
    <div>
      <div style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>
        {t.trancheName} · {t.trancheClass || '—'}
      </div>
      <div>Current Bal: <strong style={{ color: 'white' }}>{fmtCurrency(t.currentBalance)}</strong> ({row.pct.toFixed(2)}%)</div>
      <div>Current CS: <strong style={{ color: 'white' }}>{fmtPct(t.currentCreditSupportPct, 2)}</strong></div>
      {c?.triggerPct != null && (
        <div>Trigger: <strong style={{ color: 'white' }}>{fmtPct(c.triggerPct, 2)}</strong> · Cushion: <strong style={{ color: cushionColor(c.cushionPct) }}>
          {c.cushionPct != null ? `${c.cushionPct >= 0 ? '+' : ''}${c.cushionPct.toFixed(2)}%` : '—'}
        </strong></div>
      )}
      <div>Coupon: <strong style={{ color: 'white' }}>{t.couponRate != null ? `${t.couponRate.toFixed(3)}%` : (t.couponType || '—')}</strong></div>
      <div>Ratings: <strong style={{ color: 'white' }}>{[t.moodysRating, t.spRating, t.fitchRating, t.kbraRating, t.dbrsRating, t.morningstarRating].filter(Boolean).join(' / ') || '—'}</strong></div>
      <div>WAL: <strong style={{ color: 'white' }}>{t.weightedAvgLifeYears != null ? `${t.weightedAvgLifeYears.toFixed(1)} yr` : '—'}</strong></div>
    </div>
  );
};

export default CMBSManagementScreen;
